import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { login, loginWithGoogle, resetPassword } from "../lib/firebase";
import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { FirebaseError } from "firebase/app";
import "./auth.css";

function authMessage(err: unknown): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "auth/invalid-credential":
      case "auth/wrong-password":
      case "auth/user-not-found":
        return "Incorrect email or password.";
      case "auth/invalid-email":
        return "That doesn't look like a valid email.";
      case "auth/too-many-requests":
        return "Too many attempts. Please wait a moment and try again.";
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : "Something went wrong.";
}

export function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const cred = await login(email.trim(), password);
      if (!cred.user.emailVerified) {
        navigate("/verify-email", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await loginWithGoogle();
      navigate("/", { replace: true });
    } catch (err) {
      setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    setError(null);
    setNotice(null);
    if (!email.trim()) {
      setError("Enter your email above first, then click reset.");
      return;
    }
    try {
      await resetPassword(email.trim());
      setNotice("Password reset email sent. Check your inbox.");
    } catch (err) {
      setError(authMessage(err));
    }
  };

  return (
    <div className="auth">
      <div className="auth__card">
        <div className="auth__brand">
          <img className="auth__logo" src="/aisg-logo.webp" alt="AI Singapore" />
          <div>
            <div className="auth__brand-text">LADP Scoping Assistant</div>
            <div className="auth__brand-sub">AI Singapore</div>
          </div>
        </div>

        <h1 className="auth__title">Welcome back</h1>
        <p className="auth__lede">Sign in to continue scoping your project.</p>

        <form className="auth__form" onSubmit={handleSubmit}>
          {error && <div className="auth__error">{error}</div>}
          {notice && <div className="auth__notice">{notice}</div>}

          <Input
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <div className="auth__row">
            <span />
            <button type="button" className="auth__link" onClick={handleReset}>
              Forgot password?
            </button>
          </div>

          <Button type="submit" variant="primary" block loading={busy}>
            Sign in
          </Button>
        </form>

        <div className="auth__divider"><span>or</span></div>

        <Button
          type="button"
          variant="secondary"
          block
          disabled={busy}
          onClick={handleGoogle}
        >
          Continue with Google
        </Button>

        <div className="auth__footer">
          New to LADP? <Link to="/register">Create an account</Link>
        </div>
      </div>
    </div>
  );
}
